import React, {Fragment, useRef} from 'react';
import {Popover, Transition} from '@headlessui/react';
import {Link} from 'react-router-dom';
import {formatDistanceToNow} from 'date-fns';
import {NotificationBell} from './NotificationBell';
import {Notification, useNotifications} from '../../hooks/notifications/useNotifications';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface NotificationItemProps {
  notification: Notification;
  tripUrl: string | undefined;
  onClose: () => void;
}

function NotificationItem({ notification, tripUrl, onClose }: NotificationItemProps) {
  const timeAgo = formatDistanceToNow(new Date(notification.creation), { addSuffix: true });

  // If no URL, render as disabled div
  if (!tripUrl) {
    return (
      <div className="w-full text-left px-4 py-3 opacity-50 cursor-not-allowed">
        <div className="flex items-start gap-3">
          {!notification.read && (
            <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-action-primary" />
          )}
          <div className={classNames('flex-1 min-w-0', notification.read ? 'ml-5' : '')}>
            <p className="text-sm text-main line-clamp-2">{notification.message}</p>
            <p className="text-xs text-muted mt-1">{timeAgo}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Link
      to={tripUrl}
      onClick={onClose}
      className="block w-full text-left px-4 py-3 hover:bg-subtle transition-colors"
    >
      <div className="flex items-start gap-3">
        {!notification.read && (
          <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-action-primary" />
        )}
        <div className={classNames('flex-1 min-w-0', notification.read ? 'ml-5' : '')}>
          <p className="text-sm text-main line-clamp-2">{notification.message}</p>
          <p className="text-xs text-muted mt-1">{timeAgo}</p>
        </div>
      </div>
    </Link>
  );
}

export function NotificationDropdown() {
  const {
    unreadNotifications,
    readNotifications,
    unreadCount,
    hasUnread,
    isLoading,
    markAllAsRead,
    tripUrls,
  } = useNotifications();

  const wasOpenRef = useRef(false);

  // Effect to mark as read when dropdown opens
  const handleOpenChange = (open: boolean) => {
    if (open && !wasOpenRef.current && hasUnread) {
      markAllAsRead();
    }
    wasOpenRef.current = open;
  };

  return (
    <Popover className="relative">
      {({ open, close }) => {
        // Track open state changes for marking as read
        if (open !== wasOpenRef.current) {
          handleOpenChange(open);
        }

        return (
          <>
            <Popover.Button
              className="flex items-center justify-center rounded-full p-1 text-muted
                hover:text-brand focus:outline-none focus:ring-2 focus:ring-teal-500
                focus:ring-offset-2"
            >
              <span className="sr-only">View notifications</span>
              <NotificationBell unreadCount={unreadCount} isActive={open} />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute right-0 z-10 mt-3 w-80 max-w-sm">
                <div
                  className="overflow-hidden rounded-lg shadow-lg ring-1
                    ring-black ring-opacity-5"
                >
                  <div className="bg-surface">
                    <div className="border-b border-default px-4 py-3">
                      <h3 className="text-sm  text-main">Notifications</h3>
                    </div>

                    {isLoading ? (
                      <div className="px-4 py-8 text-center">
                        <div
                          className="inline-block h-6 w-6 animate-spin rounded-full
                            border-2 border-brand border-t-transparent"
                        />
                      </div>
                    ) : unreadNotifications.length === 0 && readNotifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <p className="text-sm text-muted">You're all caught up! 🎉</p>
                      </div>
                    ) : (
                      <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
                        {unreadNotifications.length > 0 && (
                          <div>
                            {unreadNotifications.map((notification) => (
                              <NotificationItem
                                key={notification.id}
                                notification={notification}
                                tripUrl={tripUrls[notification.tripId]}
                                onClose={close}
                              />
                            ))}
                          </div>
                        )}

                        {readNotifications.length > 0 && (
                          <div>
                            {unreadNotifications.length > 0 && (
                              <div className="px-4 py-2 bg-subtle">
                                <p className="text-xs  text-muted uppercase">Earlier</p>
                              </div>
                            )}
                            {readNotifications.map((notification) => (
                              <NotificationItem
                                key={notification.id}
                                notification={notification}
                                tripUrl={tripUrls[notification.tripId]}
                                onClose={close}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        );
      }}
    </Popover>
  );
}
